/**
 * Entrada pelo LINK DE CERIMÔNIA (I2) — a rota que o e-mail do signatário aponta.
 *
 * O app já tinha uma rota real, `/sign/:signerId?envelope=:envelopeId&t=:token`, onde `t` é o
 * SST CRU na URL. Aquilo serve ao workbench e ao diagnóstico, e continua valendo — mas é
 * exatamente o que não pode ir para um e-mail: e-mail é encaminhável e fica armazenado, e um SST
 * na querystring vaza por histórico, Referer e log de proxy.
 *
 * A rota nova é `/sign?c=<selector>.<verifier>`. O `c` NÃO é credencial de assinatura: é um
 * ponteiro opaco que só vale contra o servidor, de uso único. `POST /web/sign/resolve` o troca,
 * server-side, por `{ signer_id, envelope_id, token }` — e o `token` (SST) fica só em memória
 * (`setSessionToken`), nunca na barra de endereço, nunca em localStorage/sessionStorage.
 *
 * A resposta traz também `link`: o SUCESSOR do `c`, já que o apresentado foi consumido. Quem
 * escreve esse sucessor na URL é o app (`history.replaceState`), para que recarregar a página
 * continue funcionando — o resolve refeito é uma ida NOVA ao servidor, nunca uma sessão
 * ressuscitada do lado do cliente.
 */

/** Mesmo formato aceito pelo backend: dois base64url de 32 bytes separados por ponto. */
const CEREMONY_TOKEN_PATTERN = /^[A-Za-z0-9_-]{43}\.[A-Za-z0-9_-]{43}$/;

/** Proxy do Vite (vite.config.ts) → apps/api. Mesmo prefixo de `lib/api.ts`. */
const API_BASE = '/api';
const RESOLVE_TIMEOUT_MS = 10_000;

export interface CeremonyLinkRoute {
  c: string;
}

export type CeremonyLinkResolution =
  | { status: 'ready'; signerId: string; envelopeId: string; token: string; nextLink: string }
  | { status: 'stage_locked' }
  | { status: 'rejected' }
  | { status: 'rate_limited' }
  | { status: 'unavailable' };

/** `/sign?c=...` (sem `:signerId` no caminho). `null` quando não é esta rota. */
export function parseCeremonyLinkRoute(pathname: string, search: string): CeremonyLinkRoute | null {
  if (!/^\/sign\/?$/.test(pathname)) return null;
  const c = new URLSearchParams(search).get('c');
  if (!c || !CEREMONY_TOKEN_PATTERN.test(c)) return null;
  return { c };
}

interface ResolveResponseBody {
  status?: string;
  signer_id?: string;
  envelope_id?: string;
  token?: string;
  link?: string;
}

/**
 * Troca o `c` pelo SST. Nunca lança: todo desfecho vira um `status` que a tela sabe desenhar —
 * inclusive rede fora, que é indistinguível de link inválido do ponto de vista do usuário e por
 * isso NÃO recebe uma mensagem própria de "não existe".
 */
export async function resolveCeremonyLink(c: string): Promise<CeremonyLinkResolution> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/web/sign/resolve`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ c }),
      signal: AbortSignal.timeout(RESOLVE_TIMEOUT_MS),
    });
  } catch {
    return { status: 'unavailable' };
  }

  if (res.status === 429) return { status: 'rate_limited' };
  if (res.status === 503) return { status: 'unavailable' };
  if (!res.ok) return { status: 'rejected' };

  const body = (await res.json().catch(() => null)) as ResolveResponseBody | null;
  if (!body) return { status: 'unavailable' };
  if (body.status === 'stage_locked') return { status: 'stage_locked' };
  if (body.status !== 'ready' || !body.signer_id || !body.envelope_id || !body.token || !body.link) {
    return { status: 'rejected' };
  }
  return {
    status: 'ready',
    signerId: body.signer_id,
    envelopeId: body.envelope_id,
    token: body.token,
    nextLink: body.link,
  };
}

/**
 * Troca o `c` da barra de endereço pelo sucessor, sem navegar e sem empilhar histórico. O SST
 * NUNCA passa por aqui — só o ponteiro opaco, que é o que já tinha chegado pela URL.
 */
export function replaceCeremonyLinkInUrl(nextLink: string): void {
  if (typeof window === 'undefined' || !window.history?.replaceState) return;
  const url = new URL(window.location.href);
  url.searchParams.set('c', nextLink);
  window.history.replaceState(null, '', `${url.pathname}${url.search}`);
}
